import { useEffect, useRef, useState } from "react";

/**
 * One-shot intersection reveal: element becomes "revealed" the first time
 * it enters the viewport, then stays static. Honors prefers-reduced-motion.
 */
export function useScrollRevealOnce({
  threshold = 0,
  rootMargin = "0px",
} = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setRevealed(true);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);

    const revealIfVisible = () => {
      const rect = el.getBoundingClientRect();
      const vh =
        window.innerHeight || document.documentElement.clientHeight || 0;
      if (rect.bottom > 0 && rect.top < vh) {
        setRevealed(true);
        observer.disconnect();
        return true;
      }
      for (const entry of observer.takeRecords()) {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
          return true;
        }
      }
      return false;
    };

    if (!revealIfVisible()) {
      const id = requestAnimationFrame(() => {
        revealIfVisible();
      });
      return () => {
        cancelAnimationFrame(id);
        observer.disconnect();
      };
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return { ref, revealed };
}
